import React, { useEffect, useMemo, useState } from 'react';
import { Chart as ChartJS } from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { Chart } from 'primereact/chart';
import { Calendar } from 'primereact/calendar';
import { InputSwitch } from 'primereact/inputswitch';
import { useSelector, useDispatch } from 'react-redux';
import { loadTestPerformanceHeatmapAsync, selectTestPerformanceHeatmap } from '../dashboardSlice';
import { ChartCard } from './ChartCard';
import { HeatmapCellDetailDialog } from './HeatmapCellDetailDialog';
import { useThemeVersion } from '../../../useTheme';

// Idempotente — já registrado por ModulePerformanceHeatmap.js se os dois
// chegarem a montar na mesma sessão, mas cada arquivo garante o próprio
// registro (chart.js/auto não inclui o tipo "matrix").
ChartJS.register(MatrixController, MatrixElement);

const hexToRgb = (hex) => {
    const match = (hex || '').trim().match(/^#?([0-9a-f]{6})$/i);
    if (!match) return [128, 128, 128];
    const n = parseInt(match[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// Mesma escala fixa de 5 níveis de verde (estilo GitHub) e mesmo desbote por
// volume do ModulePerformanceHeatmap (ver comentário lá).
const GREEN_LEVELS = ['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39'];
const levelForValue = (value) => {
    if (value <= 0) return 0;
    if (value <= 25) return 1;
    if (value <= 50) return 2;
    if (value <= 75) return 3;
    return 4;
};

const colorForValue = (value, opacity = 1) => {
    if (value == null) return 'rgba(128, 128, 128, 0.15)';
    const [r, g, b] = hexToRgb(GREEN_LEVELS[levelForValue(value)]);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const MIN_VOLUME_OPACITY = 0.35;
const opacityForVolume = (total, maxTotal) => {
    if (!maxTotal || total == null) return 1;
    const ratio = Math.max(0, Math.min(1, total / maxTotal));
    return MIN_VOLUME_OPACITY + ratio * (1 - MIN_VOLUME_OPACITY);
};

// Mesmo formato usado em StudentsRankingTable.js pro filtro de período.
const formatDateParam = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const testLabel = (cell) => {
    const title = cell.testTitle || `Prova ${cell.testId}`;
    return cell.testYear ? `${title} (${cell.testYear})` : title;
};

const buildDataset = (cells, periods, tests, field, totalField, label, surfaceHex) => {
    const points = cells.filter(cell => cell[field] != null);
    const maxTotal = points.reduce((max, cell) => Math.max(max, cell[totalField] || 0), 0);
    return {
        datasets: [{
            label,
            data: points.map(cell => ({ x: cell.period, y: testLabel(cell), v: cell[field], total: cell[totalField], testId: cell.testId })),
            backgroundColor: (ctx) => {
                const raw = ctx.raw;
                const opacity = opacityForVolume(raw?.total, maxTotal);
                return colorForValue(raw?.v, opacity);
            },
            borderWidth: 1,
            borderColor: surfaceHex,
            width: ({ chart }) => Math.max(((chart.chartArea || {}).width || 0) / Math.max(periods.length, 1) - 2, 4),
            height: ({ chart }) => Math.max(((chart.chartArea || {}).height || 0) / Math.max(tests.length, 1) - 2, 4),
        }],
    };
};

// Ver comentário equivalente em ModulePerformanceHeatmap.js.
const isoWeekStart = (week, year) => {
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const start = new Date(simple);
    if (dow <= 4) start.setDate(simple.getDate() - dow + 1);
    else start.setDate(simple.getDate() + 8 - dow);
    return start;
};

const formatShortDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
};

const weekRangeLabel = (period) => {
    const match = /^(\d{4})-W(\d{2})$/.exec(period || '');
    if (!match) return null;
    const start = isoWeekStart(Number(match[2]), Number(match[1]));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${formatShortDate(start)} a ${formatShortDate(end)}`;
};

// period vem do backend como "AAAA-Wss" (semana em inglês) — troca só o "W"
// por "S" (Semana) pro rótulo do eixo, sem mexer no valor usado pelo Chart.js
// pra casar cada ponto com sua categoria (esse continua sendo "period" cru).
const formatPeriodTick = (period) => (period || '').replace(/^(\d{4})-W(\d{2})$/, '$1-S$2');

const buildOptions = (periods, tests, textColor, onCellClick) => ({
    maintainAspectRatio: false,
    onClick: onCellClick ? (event, elements) => {
        if (!elements || !elements.length) return;
        const point = event.chart.data.datasets[elements[0].datasetIndex]?.data[elements[0].index];
        if (point) setTimeout(() => onCellClick(point), 0);
    } : undefined,
    onHover: onCellClick ? (event, elements) => {
        event.native.target.style.cursor = elements.length ? 'pointer' : 'default';
    } : undefined,
    scales: {
        x: {
            type: 'category',
            labels: periods,
            offset: true,
            ticks: { color: textColor, callback: (value, index) => formatPeriodTick(periods[index]) },
            grid: { display: false }
        },
        y: {
            type: 'category',
            labels: tests,
            offset: true,
            ticks: { color: textColor },
            grid: { display: false }
        }
    },
    plugins: {
        legend: false,
        tooltip: {
            callbacks: {
                title: () => '',
                label: (ctx) => {
                    const raw = ctx.raw;
                    const range = weekRangeLabel(raw.x);
                    const weekLabel = range ? `Semana ${raw.x} (${range})` : `Semana ${raw.x}`;
                    if (raw.v == null) return `${raw.y} — ${weekLabel}: sem dados`;
                    const total = raw.total ?? 0;
                    return `${raw.y} — ${weekLabel}: ${raw.v}% de acerto (${total} resposta${total === 1 ? '' : 's'})`;
                }
            }
        }
    }
});

// Reaproveitado pelo heatmap agregado (tela de Desempenho, sem userId) e
// pelo individual (tela de heatmap do aluno, com userId) — mesmo componente,
// o filtro que muda. Espelha ModulePerformanceHeatmap.js, agrupado por
// prova em vez de módulo. showReload controla o botão de recarregar do
// card — a tela do aluno prefere um único botão geral no topo da página
// (StudentHeatmapPage) em vez de um por card.
export const TestPerformanceHeatmap = ({ userId, showReload = true } = {}) => {
    const dispatch = useDispatch();
    const { data, status, error, filters } = useSelector(selectTestPerformanceHeatmap);
    const loading = status === 'loading';
    const themeVersion = useThemeVersion();
    const [selectedCell, setSelectedCell] = useState(null);
    const [dateRange, setDateRange] = useState(
        filters?.from && filters?.to ? [new Date(filters.from), new Date(filters.to)] : null
    );
    // Ver comentário equivalente em ModulePerformanceHeatmap.js.
    useEffect(() => {
        if (filters?.from && filters?.to) {
            setDateRange([new Date(filters.from), new Date(filters.to)]);
        }
    }, [filters?.from, filters?.to]);

    const applyFilters = (newFilters) => {
        dispatch(loadTestPerformanceHeatmapAsync({ ...filters, userId, ...newFilters }));
    };
    const onReload = () => applyFilters({});

    // Ver comentário equivalente em ModulePerformanceHeatmap.js.
    const onDateRangeChange = (e) => {
        const value = e.value ?? null;
        setDateRange(value);
        const [start, end] = value || [];
        if (start && end) {
            applyFilters({ from: formatDateParam(start), to: formatDateParam(end) });
        } else if (!start && !end) {
            applyFilters({ from: null, to: null });
        }
    };

    const cells = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const { periods, tests } = useMemo(() => {
        const periodSet = new Set();
        const testSet = new Set();
        cells.forEach(cell => {
            periodSet.add(cell.period);
            testSet.add(testLabel(cell));
        });
        return {
            periods: Array.from(periodSet).sort(),
            tests: Array.from(testSet).sort()
        };
    }, [cells]);

    const { surfaceHex, textColor } = useMemo(() => {
        const style = getComputedStyle(document.documentElement);
        return {
            surfaceHex: style.getPropertyValue('--surface-card').trim() || '#fcfcfb',
            textColor: style.getPropertyValue('--text-color').trim() || '#495057'
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeVersion]);

    const quizData = useMemo(
        () => buildDataset(cells, periods, tests, 'quizCorrectPercentage', 'totalQuizAnswers', 'Quiz', surfaceHex),
        [cells, periods, tests, surfaceHex]
    );
    const practiceTestData = useMemo(
        () => buildDataset(cells, periods, tests, 'practiceTestCorrectPercentage', 'totalPracticeTestAnswers', 'Simulado', surfaceHex),
        [cells, periods, tests, surfaceHex]
    );
    // Clique só faz sentido no heatmap por aluno (userId presente). Cada
    // gráfico (Quiz/Simulado) grava seu próprio `source` — ver comentário
    // equivalente em ModulePerformanceHeatmap.js.
    const onQuizCellClick = userId
        ? (point) => setSelectedCell({ testId: point.testId, period: point.x, source: 'quiz', label: `${point.y} — Semana ${point.x}` })
        : undefined;
    const onPracticeTestCellClick = userId
        ? (point) => setSelectedCell({ testId: point.testId, period: point.x, source: 'practiceTest', label: `${point.y} — Semana ${point.x}` })
        : undefined;

    const quizChartOptions = useMemo(
        () => buildOptions(periods, tests, textColor, onQuizCellClick),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [periods, tests, textColor, userId]
    );
    const practiceTestChartOptions = useMemo(
        () => buildOptions(periods, tests, textColor, onPracticeTestCellClick),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [periods, tests, textColor, userId]
    );

    return (
        <>
            <div className="flex flex-wrap gap-3 mb-3">
                <div className="flex flex-column gap-1">
                    <label htmlFor="test-heatmap-date-range" className="text-xs text-color-secondary">Período</label>
                    <Calendar
                        id="test-heatmap-date-range"
                        value={dateRange}
                        onChange={onDateRangeChange}
                        selectionMode="range"
                        dateFormat="dd/mm/yy"
                        placeholder="Todo o período"
                        showButtonBar
                        disabled={loading}
                        className="w-full md:w-20rem"
                    />
                </div>
                {!userId && (
                    <div className="flex align-items-center gap-2 align-self-end pb-2">
                        <InputSwitch
                            inputId="test-heatmap-active-only"
                            checked={!!filters?.activeOnly}
                            onChange={(e) => applyFilters({ activeOnly: e.value })}
                            disabled={loading}
                        />
                        <label htmlFor="test-heatmap-active-only" className="text-xs text-color-secondary">Somente ativos</label>
                    </div>
                )}
            </div>
            <ChartCard title="Quiz — Heatmap por Prova/Semana" loading={loading} error={error} onReload={showReload ? onReload : undefined}>
                <Chart type="matrix" data={quizData} options={quizChartOptions} height="400px" />
            </ChartCard>
            <div className="mt-3">
                <ChartCard title="Simulado — Heatmap por Prova/Semana" loading={loading} error={error}>
                    <Chart type="matrix" data={practiceTestData} options={practiceTestChartOptions} height="400px" />
                </ChartCard>
            </div>
            {userId && (
                <HeatmapCellDetailDialog
                    visible={!!selectedCell}
                    onHide={() => setSelectedCell(null)}
                    userId={userId}
                    testId={selectedCell?.testId}
                    period={selectedCell?.period}
                    source={selectedCell?.source}
                    cellLabel={selectedCell?.label}
                />
            )}
        </>
    );
};
